import { getBundle, listBundles, archiveBundlesForPhases } from "@/lib/api/quarterlyBundles";
import { listOrganizationCurriculum } from "@/lib/api/organizationCurriculum";
import { getVideo } from "@/lib/api/videos";
import {
  assignCurriculumDisplayStatuses,
  calendarDateTodayUtc,
  extractIntroPhases,
  supersededPhaseIds,
  type OrganizationCurriculumWithStatus,
} from "@/lib/curriculumStatus";
import type { OrganizationCurriculumRow, QuarterlyBundleRow, VideoRow } from "@/types/database";

export type FeaturedPlayerKind = "maps" | "education";

/**
 * Resolved consumption view for org-assigned curriculum.
 *
 * HQ / Super Admin assigns curriculum by authoring `organization_curriculum` rows (and linked
 * `quarterly_bundles`) for the organization — there is no separate assignment table. This helper
 * loads those rows and resolves **one** featured playback surface plus Self Care Library inputs.
 *
 * Resolution rules (featured main player priority):
 * 1. **MAPS bundle** — When a timed phase is active and a MAPS bundle resolves for that phase
 *    (**published** bundles linked via `curriculum_phase_id`: job-category match → org-wide `job_category_id`
 *    null → **when no viewer category**, first linked published bundle for org-preview surfaces — then
 *    `exercise_bundle_id` on the phase row). MAPS always wins over phase Education when any bundle resolves.
 * 2. **Education** — Same timed phase: if no MAPS bundle, use that phase’s education chain
 *    (intro / main / outro) when `education_video_id` is set.
 * 3. **Results-only fallback (timed phase)** — If the active numbered phase has neither MAPS nor phase
 *    education, feature **Results review** only when `education_video_id` is set. **Orientation stays
 *    library-only** whenever any numbered phase is active (employees open it from Self Care Library /
 *    archive sections).
 * 4. **No numbered phase yet** — Featured slot uses the next upcoming phase's published bundle, else
 *    the org's newest published body-region bundle; only when neither exists, **Results** →
 *    **Orientation** education (same precedence as the intro pipeline).
 *
 * MAPS uses playlist/thumbnail Studio chrome; education paths use chained playback only (no MAPS rail).
 *
 * Employees never resolve unpublished/orphan bundles outside **`organization_curriculum`** linkage:
 * if nothing qualifies for featured playback, the portal stays empty until HQ assigns content.
 *
 * Lazy archival (plan §6.3): superseded phase bundles are archived on read before status logic runs.
 */
export type ActiveCurriculumForEmployee = {
  /** Active timed phase row (Phase 1+ pipeline), if any. At most one globally. */
  curriculum: OrganizationCurriculumWithStatus | null;
  /**
   * Source row when featured education is **Results** or **Orientation** on the main player (never MAPS).
   * With an active numbered phase, only Results can appear here as fallback; Orientation is library-only.
   * Null when featured MAPS or phase-owned education.
   */
  introHeroPhase: OrganizationCurriculumRow | null;
  featuredPlayer: FeaturedPlayerKind | null;
  /** True when featured MAPS uses full playlist chrome (`BundlePlayer` rail). Always false for education-only hero. */
  showMapsPlaylistThumbnails: boolean;
  bundle: QuarterlyBundleRow | null;
  /** Set when education owns the featured slot (timed phase without MAPS, or intro hero). */
  educationVideo: VideoRow | null;
  introVideo: VideoRow | null;
  outroVideo: VideoRow | null;
  orientation: OrganizationCurriculumRow | null;
  results: OrganizationCurriculumRow | null;
  allWithStatus: OrganizationCurriculumWithStatus[];
  /** Any curriculum rows exist for the org (RLS-scoped); surfaces softer empty states vs truly unprovisioned orgs. */
  hasOrganizationAssignments: boolean;
};

/**
 * Pick one featured MAPS bundle from rows already scoped to the phase (typically published-only).
 * Employees: category-specific → org-wide (null job_category_id). No silent mismatch across categories.
 * Org-wide / preview (`jobCategoryId` null): org-wide bundle first, else first row (listBundles is newest-first).
 */
function pickFeaturedBundleForPhase(
  phaseBundles: QuarterlyBundleRow[],
  jobCategoryId: string | null,
): QuarterlyBundleRow | null {
  if (phaseBundles.length === 0) return null;

  if (jobCategoryId) {
    const forCategory = phaseBundles.find((b) => b.job_category_id === jobCategoryId);
    if (forCategory) return forCategory;
    const orgWide = phaseBundles.find((b) => b.job_category_id === null);
    return orgWide ?? null;
  }

  const orgWide = phaseBundles.find((b) => b.job_category_id === null);
  if (orgWide) return orgWide;
  return phaseBundles[0] ?? null;
}

export async function resolvePublishedMapsBundleForPhase(
  curriculum: OrganizationCurriculumWithStatus,
  orgId: string,
  jobCategoryId: string | null,
): Promise<QuarterlyBundleRow | null> {
  const phaseBundles = await listBundles({
    organization_id: orgId,
    curriculum_phase_id: curriculum.id,
    status: "published",
  });

  if (import.meta.env.DEV) {
    console.log("[Fit2Lift curriculum resolver] MAPS bundle lookup", {
      phaseId: curriculum.id,
      phaseTitle: curriculum.title,
      phaseNumber: curriculum.phase_number,
      exercise_bundle_id: curriculum.exercise_bundle_id,
      jobCategoryId,
      poster_url: curriculum.poster_url ?? null,
      poster_thumbnail_url: curriculum.poster_thumbnail_url ?? null,
      publishedBundlesLinkedToPhase: phaseBundles.map((b) => ({
        id: b.id,
        title: b.title,
        status: b.status,
        job_category_id: b.job_category_id,
        curriculum_phase_id: b.curriculum_phase_id,
      })),
    });
  }

  let bundle = pickFeaturedBundleForPhase(phaseBundles, jobCategoryId);

  if (!bundle && curriculum.exercise_bundle_id) {
    const linked = await getBundle(curriculum.exercise_bundle_id);
    if (import.meta.env.DEV) {
      console.log("[Fit2Lift curriculum resolver] MAPS exercise_bundle_id fallback", {
        exercise_bundle_id: curriculum.exercise_bundle_id,
        loaded: linked
          ? {
              id: linked.id,
              title: linked.title,
              status: linked.status,
              curriculum_phase_id: linked.curriculum_phase_id,
            }
          : null,
      });
    }
    if (linked?.status === "published") {
      bundle = linked;
    }
  }

  if (import.meta.env.DEV) {
    console.log("[Fit2Lift curriculum resolver] MAPS picked bundle", {
      bundleId: bundle?.id ?? null,
      bundleTitle: bundle?.title ?? null,
    });
  }

  return bundle;
}

/** Intro pipeline row for fallback: Results wins over Orientation when both exist. */
function pipelineIntroHero(
  results: OrganizationCurriculumRow | null,
  orientation: OrganizationCurriculumRow | null,
): OrganizationCurriculumRow | null {
  return results ?? orientation ?? null;
}

export async function loadEducationChain(row: OrganizationCurriculumRow): Promise<{
  educationVideo: VideoRow | null;
  introVideo: VideoRow | null;
  outroVideo: VideoRow | null;
}> {
  if (!row.education_video_id) {
    return { educationVideo: null, introVideo: null, outroVideo: null };
  }
  const [educationVideo, introVideo, outroVideo] = await Promise.all([
    getVideo(row.education_video_id),
    row.intro_video_id ? getVideo(row.intro_video_id) : Promise.resolve(null),
    row.outro_video_id ? getVideo(row.outro_video_id) : Promise.resolve(null),
  ]);
  return { educationVideo, introVideo, outroVideo };
}

/**
 * Before any timed phase starts, so the employee home always has an exercise program to show:
 * the bundle of the **next** upcoming phase (earliest start date, so Phase 3 never shows before
 * Phase 1); failing that, the org's newest published body-region bundle. Same job-category rule
 * as phase bundles. Soft-fails to null.
 */
async function resolvePreStartFallbackBundle(
  upcomingPhases: OrganizationCurriculumWithStatus[],
  orgId: string,
  jobCategoryId: string | null,
): Promise<QuarterlyBundleRow | null> {
  try {
    const byStart = [...upcomingPhases].sort((a, b) =>
      String(a.start_date ?? "").localeCompare(String(b.start_date ?? "")),
    );
    for (const phase of byStart) {
      const phaseBundle = await resolvePublishedMapsBundleForPhase(phase, orgId, jobCategoryId);
      if (phaseBundle) return phaseBundle;
    }
    const rows = await listBundles({ organization_id: orgId, status: "published" });
    return pickFeaturedBundleForPhase(
      rows.filter((b) => b.body_region_focus != null),
      jobCategoryId,
    );
  } catch (e) {
    console.warn("[resolveActiveCurriculumForEmployee] pre-start bundle fallback failed", e);
    return null;
  }
}

export async function resolveActiveCurriculumForEmployee(
  orgId: string,
  jobCategoryId: string | null,
): Promise<ActiveCurriculumForEmployee> {
  const today = calendarDateTodayUtc();
  const raw = await listOrganizationCurriculum(orgId);
  const hasOrganizationAssignments = raw.length > 0;
  const { orientation, results, phaseRows } = extractIntroPhases(raw);

  const superseded = supersededPhaseIds(raw, today);
  if (superseded.length > 0) {
    try {
      await archiveBundlesForPhases(superseded);
    } catch (e) {
      console.warn("[resolveActiveCurriculumForEmployee] archival failed", e);
    }
  }

  const allWithStatus = assignCurriculumDisplayStatuses(phaseRows, today);
  const timedActive = allWithStatus.find((r) => r.displayStatus === "active") ?? null;

  let bundle: QuarterlyBundleRow | null = null;
  let educationVideo: VideoRow | null = null;
  let introVideo: VideoRow | null = null;
  let outroVideo: VideoRow | null = null;
  let featuredPlayer: FeaturedPlayerKind | null = null;
  let showMapsPlaylistThumbnails = false;
  let introHeroPhase: OrganizationCurriculumRow | null = null;
  const pipelineIntro = pipelineIntroHero(results, orientation);

  if (timedActive) {
    bundle = await resolvePublishedMapsBundleForPhase(timedActive, orgId, jobCategoryId);
    if (bundle) {
      featuredPlayer = "maps";
      showMapsPlaylistThumbnails = true;
    } else if (timedActive.education_video_id) {
      featuredPlayer = "education";
      const chain = await loadEducationChain(timedActive);
      educationVideo = chain.educationVideo;
      introVideo = chain.introVideo;
      outroVideo = chain.outroVideo;
    } else if (results?.education_video_id) {
      featuredPlayer = "education";
      introHeroPhase = results;
      const chain = await loadEducationChain(results);
      educationVideo = chain.educationVideo;
      introVideo = chain.introVideo;
      outroVideo = chain.outroVideo;
    }
  } else if (
    (bundle = await resolvePreStartFallbackBundle(
      allWithStatus.filter((r) => r.displayStatus === "upcoming"),
      orgId,
      jobCategoryId,
    ))
  ) {
    // No phase has started yet: feature an exercise bundle ahead of Orientation/Results,
    // which stay reachable from the Program library.
    featuredPlayer = "maps";
    showMapsPlaylistThumbnails = true;
  } else if (pipelineIntro?.education_video_id) {
    introHeroPhase = pipelineIntro;
    featuredPlayer = "education";
    const chain = await loadEducationChain(pipelineIntro);
    educationVideo = chain.educationVideo;
    introVideo = chain.introVideo;
    outroVideo = chain.outroVideo;
  }

  if (import.meta.env.DEV) {
    console.log("[Fit2Lift curriculum resolver] playback summary", {
      orgId,
      jobCategoryId,
      timedActivePhaseId: timedActive?.id ?? null,
      timedActivePhaseTitle: timedActive?.title ?? null,
      timedPhasePoster: timedActive
        ? {
            poster_url: timedActive.poster_url ?? null,
            poster_thumbnail_url: timedActive.poster_thumbnail_url ?? null,
          }
        : null,
      featuredPlayer,
      mapsBundleId: bundle?.id ?? null,
      mapsBundleTitle: bundle?.title ?? null,
      educationVideoId: educationVideo?.id ?? null,
      educationVideoTitle: educationVideo?.title ?? null,
      introHeroPhaseId: introHeroPhase?.id ?? null,
      introHeroPoster: introHeroPhase
        ? {
            poster_url: introHeroPhase.poster_url ?? null,
            poster_thumbnail_url: introHeroPhase.poster_thumbnail_url ?? null,
          }
        : null,
    });
  }

  return {
    curriculum: timedActive,
    introHeroPhase,
    featuredPlayer,
    showMapsPlaylistThumbnails,
    bundle,
    educationVideo,
    introVideo,
    outroVideo,
    orientation,
    results,
    allWithStatus,
    hasOrganizationAssignments,
  };
}

/** True when the resolver produced a single featured MAPS or education surface. */
export function hasFeaturedPlayback(data: ActiveCurriculumForEmployee | null): boolean {
  if (!data?.featuredPlayer) return false;
  if (data.featuredPlayer === "maps") return Boolean(data.bundle);
  return Boolean(data.educationVideo);
}
